import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api, signedInEmail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ShopBuyModal({ product, onClose, source = "shop", addon = null }) {
  const [email, setEmail] = useState("");
  const [withAddon, setWithAddon] = useState(false);
  const total = product.amount + (withAddon && addon ? addon.amount : 0);
  const [busy, setBusy] = useState(false);
  useEffect(() => { signedInEmail().then((e) => e && setEmail((cur) => cur || e)); }, []);

  const confirmBuy = async () => {
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) { toast.error("Enter a valid email for your receipt."); return; }
    setBusy(true);
    const items = [{ item_id: product.lookup_key, item_name: product.name, price: product.amount / 100 }];
    if (withAddon && addon) items.push({ item_id: addon.lookup_key, item_name: addon.name, price: addon.amount / 100 });
    trackEvent("begin_checkout", { currency: "USD", value: total / 100, items });
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: product.lookup_key, origin_url: window.location.origin, email: clean, source,
        ...(withAddon && addon ? { addons: [addon.lookup_key] } : {}),
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Couldn't start checkout. Try again.");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1A1A1A]/70" onClick={() => !busy && onClose()} data-testid="shop-email-modal">
      <div className="bg-[#F3F1EC] border-2 border-[#1E1E1E] max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b-2 border-[#D3CFC0]">
          <div>
            <span className="kicker">Almost there</span>
            <h2 className="font-head font-semibold text-xl text-[#1E1E1E] leading-tight mt-1">{product.name}{withAddon && addon ? ` + ${addon.name}` : ""}</h2>
            <span className={`${addon ? "mt-2 block " : ""}font-head text-2xl text-[#1E1E1E]`} data-testid="shop-modal-total">{addon ? "Total " : ""}${total / 100}</span>
          </div>
          <button onClick={onClose} data-testid="shop-email-modal-close" className="text-[#6E6E6E] hover:text-[#1E1E1E] shrink-0"><X size={22} /></button>
        </div>
        <div className="p-5">
          {addon && (
            <label className="mb-4 flex items-start gap-3 border-2 border-[#D3CFC0] bg-white p-3 cursor-pointer" data-testid="addon-row">
              <input type="checkbox" checked={withAddon} onChange={(e) => setWithAddon(e.target.checked)}
                className="mt-1 h-4 w-4 accent-[#E8A33D]" data-testid="addon-checkbox" />
              <span className="text-sm text-[#1E1E1E]">
                <strong>Add the {addon.name}: ${addon.amount / 100}</strong>
                <span className="block text-[#6E6E6E]">{addon.blurb}</span>
              </span>
            </label>
          )}
          <label className="kicker">Email for your receipt & download</label>
          <input type="email" value={email} autoFocus onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirmBuy(); }}
            data-testid="shop-email-input"
            className="mt-1 w-full border-2 border-[#D3CFC0] bg-[#E7E3D9] px-4 py-3 focus:outline-none focus:border-[#2D2D2D]" placeholder="you@email.com" />
          <button onClick={confirmBuy} disabled={busy} data-testid="shop-checkout-btn"
            className="mt-4 w-full bg-[#E8A33D] text-[#1A1A1A] font-head uppercase tracking-wide text-sm py-3 hover:bg-[#D6922C] transition-colors disabled:opacity-60">
            {busy ? "Starting checkout…" : "Continue to checkout"}
          </button>
          <p className="mt-2 text-xs text-[#6E6E6E] text-center">Secure checkout via Stripe. We'll email your receipt here.</p>
        </div>
      </div>
    </div>
  );
}
